import { _, helpers, mui, React, ts, ui } from '_core';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  fullScreen: boolean;
  title: string;
};

const Chart = ({ id, readOnly, widgetKey, loadWidgetData, file, fullScreen, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [selectedOuterTab, setSelectedOuterTab] = React.useState<'importance' | 'rank'>('importance');
  const [selectedInnerTab, setSelectedInnerTab] = React.useState<'time_series' | 'avg'>('time_series');

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: { file },
      });
      if (response) {
        const newData = _.map(response.data, (row) => ({
          ..._.omit(row, 'build_date'),
          date: row.build_date,
        }));
        setData(newData);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  const chartData = React.useMemo(() => {
    let metric;
    if (selectedOuterTab == 'importance' && selectedInnerTab == 'time_series') {
      metric = 'importance';
    } else if (selectedOuterTab == 'rank' && selectedInnerTab == 'time_series') {
      metric = 'rank';
    } else if (selectedOuterTab == 'importance' && selectedInnerTab == 'avg') {
      metric = 'avg_importance';
    } else {
      /* if (selectedOuterTab == 'rank' && selectedInnerTab == 'avg') */
      metric = 'avg_rank';
    }

    const newData = data === null ? [] : data.filter((row) => row.metric == metric);

    if (metric == 'importance' || metric == 'rank') {
      return newData;
    }

    return Object.entries(newData[0])
      .filter(([key]) => key !== 'metric' && key !== 'date')
      .map(([signal, value]) => ({
        signal,
        value,
      }))
      .sort((a, b) => {
        return metric === 'avg_importance'
          ? (b.value as number) - (a.value as number)
          : (a.value as number) - (b.value as number);
      });
  }, [data, selectedOuterTab, selectedInnerTab]);

  const getChart = () => {
    if (selectedInnerTab == 'time_series') {
      return (
        <ui.LineChart
          id={id}
          data={chartData}
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={title}
          withoutSeries={['metric']}
        />
      );
    }

    return (
      <ui.NestedBarChart
        id={id}
        fullScreen={fullScreen}
        category="signal"
        data={chartData}
        readOnly={readOnly}
        alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
        numberFormat={'#.##'}
        displayNumbers={true}
        horizontal={true}
        hideLegend={true}
        tooltipText="{signal}: {value}"
      />
    );
  };

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }} key={widgetKey}>
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedOuterTab}
        onChange={(_e, newTab) => setSelectedOuterTab(newTab)}
      >
        <mui.core.Tab label="Importance" value="importance" />
        <mui.core.Tab label="Rank" value="rank" />
      </mui.core.Tabs>
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedInnerTab}
        onChange={(_e, newTab) => setSelectedInnerTab(newTab)}
      >
        <mui.core.Tab label="Time Series" value="time_series" />
        <mui.core.Tab label="Average" value="avg" />
      </mui.core.Tabs>
      {getChart()}
    </mui.core.Box>
  );
};

export default Chart;
